import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import ProgressBar from '../../../components/common/ProgressBar';
import CompanyRegistration from './steps/CompanyRegistration';
import AdminSetup from './steps/AdminSetup';
import SubscriptionSelector from './steps/SubscriptionSelector';
import SeatAllocation from './steps/SeatAllocation';
import PaymentProcessor from './steps/PaymentProcessor';
import TeamInvites from './steps/TeamInvites';
import SignupSuccess from './steps/SignupSuccess';
import { authAPI } from '../../../services/api';

const STEPS = [
  { id: 'company', title: 'Company Details' },
  { id: 'admin', title: 'Admin Setup' },
  { id: 'subscription', title: 'Choose Plan' },
  { id: 'seats', title: 'Seat Allocation' },
  { id: 'payment', title: 'Payment' },
  { id: 'team', title: 'Invite Team' },
  { id: 'success', title: 'All Set!' }
];

export default function SignupFlow({ user }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    company: {
      name: '',
      domain: user?.email ? user.email.split('@')[1] : '', // Pre-fill domain from email
    },
    admin: {
      email: user?.email || '',
      name: user?.name || '',
    },
    subscription: {
      planId: '',
      planName: '',
      billingPeriod: 'monthly',
    },
    seats: {
      count: 1,
    },
    payment: {
      paymentMethodId: '',
      completed: false,
    },
    team: [],
  });

  const handleStepComplete = (stepId, data) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: data
    }));
    
    // If this is the payment step, submit the entire form
    if (stepId === 'payment') {
      handleFormSubmit();
    } else if (stepId === 'team' || currentStep === STEPS.length - 2) {
      // If this is the team step or we're at the second-to-last step, go to success
      setCurrentStep(STEPS.length - 1);
    } else {
      // Otherwise, go to the next step
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleFormSubmit = async () => {
    try {
      // Submit the form data to create organization, subscription, etc.
      const response = await authAPI.completeSignup({
        company: formData.company,
        admin: formData.admin,
        subscription: {
          ...formData.subscription,
          seats: formData.seats.count,
          paymentMethodId: formData.payment.paymentMethodId,
        },
        team: formData.team,
      });
      
      console.log('Signup completed successfully:', response);
      
      // Go to success step
      setCurrentStep(STEPS.length - 1);
    } catch (error) {
      console.error('Failed to complete signup:', error);
      // Handle error (could add error state and display)
    }
  };

  const handleSkip = () => {
    // Skip to the next step
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    // Go back to the previous step
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    // Redirect to dashboard
    navigate('/dashboard');
  };

  // Render the current step
  const renderStep = () => {
    const stepId = STEPS[currentStep].id;
    
    switch (stepId) {
      case 'company':
        return (
          <CompanyRegistration 
            onComplete={(data) => handleStepComplete('company', data)} 
            initialData={formData.company}
          />
        );
      case 'admin':
        return (
          <AdminSetup 
            onComplete={(data) => handleStepComplete('admin', data)} 
            initialData={formData.admin}
            companyDomain={formData.company.domain}
          />
        );
      case 'subscription':
        return (
          <SubscriptionSelector 
            onComplete={(data) => handleStepComplete('subscription', data)} 
            initialData={formData.subscription}
            onBack={handleBack}
          />
        );
      case 'seats':
        return (
          <SeatAllocation 
            onComplete={(data) => handleStepComplete('seats', data)} 
            initialData={formData.seats}
            planDetails={formData.subscription}
            onBack={handleBack}
          />
        );
      case 'payment':
        return (
          <PaymentProcessor 
            onComplete={(data) => handleStepComplete('payment', data)} 
            subscriptionData={{
              ...formData.subscription,
              seats: formData.seats.count
            }}
            onBack={handleBack}
          />
        );
      case 'team':
        return (
          <TeamInvites 
            onComplete={(data) => handleStepComplete('team', data)} 
            initialData={formData.team}
            companyDomain={formData.company.domain}
            onBack={handleBack}
            onSkip={() => handleStepComplete('team', [])}
          />
        );
      case 'success':
        return (
          <SignupSuccess 
            organizationName={formData.company.name}
            onFinish={handleFinish}
          />
        );
      default:
        return null;
    }
  };

  // Determine if the current step needs a wider container
  const isWideStep = STEPS[currentStep]?.id === 'payment' || STEPS[currentStep]?.id === 'subscription';

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand to-white py-12">
      <div className={`mx-auto px-4 ${isWideStep ? 'max-w-6xl' : 'max-w-2xl'}`}>
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-primary" />
          <span className="text-2xl font-bold text-primary">Stickr</span>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-navy mb-4">
            {STEPS[currentStep].title}
          </h1>
          <ProgressBar 
            value={currentStep + 1} 
            max={STEPS.length}
            className="mt-4"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-stickr-sm p-8">
          {renderStep()}
        </div>
      </div>
    </div>
  );
} 